import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { SegmentedControl } from './segmented-control';
import { Repeater, RepeaterTitle } from './repeater';
import { ImageUploadField } from './image-upload-field';

type GalleryBlock = Extract<Block, { type: 'gallery' }>;
type GalleryProps = GalleryBlock['props'];
type ImageRef = GalleryProps['images'][number];

const LAYOUT_OPTIONS = [
  { value: 'grid' as const, label: 'Grid' },
  { value: 'masonry' as const, label: 'Masonry' },
  { value: 'carousel' as const, label: 'Carousel' },
];

function newImage(): ImageRef {
  return { url: '', alt: '' };
}

interface GalleryInspectorProps {
  block: GalleryBlock;
  onChange: (next: GalleryProps) => void;
}

export function GalleryInspector({ block, onChange }: GalleryInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<GalleryProps>) => onChange({ ...props, ...next });

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-4">
          <InspectorField
            label="Title"
            value={props.title ?? ''}
            placeholder="Our work"
            onChange={(v) => patch({ title: v })}
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Layout</span>
            <SegmentedControl
              ariaLabel="Layout"
              value={props.layout}
              options={LAYOUT_OPTIONS}
              onChange={(v) => patch({ layout: v })}
            />
          </div>
        </div>
      </InspectorSection>

      <InspectorSection label="Images">
        <Repeater
          items={props.images}
          onChange={(images) => patch({ images: images as GalleryProps['images'] })}
          addLabel="Add image"
          makeNew={newImage}
          renderTitle={(img, index) => (
            <RepeaterTitle>{img.alt || `Image ${index + 1}`}</RepeaterTitle>
          )}
          renderForm={(img, _i, update) => (
            <>
              <ImageUploadField
                label="Image"
                url={img.url}
                onChange={(v) => update({ url: v })}
              />
              <InspectorField
                label="Alt text"
                value={img.alt}
                placeholder="Bathroom renovation, before / after"
                helperText="Describe the image for screen readers and SEO"
                onChange={(v) => update({ alt: v })}
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
